import { useTranslation } from "react-i18next";
import { MoneyTime, MoneyChange, Money } from "iconsax-react";
import { formatMoneyKzt, type Locale } from "@/shared/utils";
import type { IncomeStats } from "../../../hooks/useIncomeListPage";

interface Props {
  stats: IncomeStats;
}

function formatDateRange(start: string, end: string, locale: Locale): string {
  const fmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" });
  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  const year = endDate.getFullYear();

  const startStr = fmt.format(startDate);
  const endStr = fmt.format(endDate);

  function capitalize(s: string): string {
    return s.replace(/(\d+\s)(\p{L})/u, (_m, nums, letter) => nums + letter.toUpperCase());
  }

  return `${capitalize(startStr)} - ${capitalize(endStr)}, ${year}`;
}

export default function IncomeStatCards({ stats }: Props) {
  const { t, i18n } = useTranslation("IncomePage");
  const locale = (i18n.language as Locale) || "ru";

  const cards = [
    {
      key: "monthly",
      label: t("stats.monthlyIncome"),
      value: formatMoneyKzt(stats.monthlyAmount, locale),
      subtitle: formatDateRange(stats.monthRange.start, stats.monthRange.end, locale),
      icon: MoneyTime,
    },
    {
      key: "quarterly",
      label: t("stats.quarterlyIncome"),
      value: formatMoneyKzt(stats.quarterlyAmount, locale),
      subtitle: formatDateRange(stats.quarterRange.start, stats.quarterRange.end, locale),
      icon: MoneyChange,
    },
    {
      key: "yearly",
      label: t("stats.yearlyIncome"),
      value: formatMoneyKzt(stats.yearlyAmount, locale),
      subtitle: formatDateRange(stats.yearRange.start, stats.yearRange.end, locale),
      icon: Money,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
      {cards.map(function renderCard(card) {
        const Icon = card.icon;
        return (
          <div key={card.key} className="surface-container-fill radius-3xl p-5 flex flex-col gap-3">
            <div className="flex justify-start items-center gap-2">
              <div className="w-8 h-8 radius-xs surface-component-fill flex items-center justify-center content-action-neutral shrink-0">
                <Icon size={16} color="currentColor" />
              </div>
              <p className="text-label-md content-base-primary">{card.label}</p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-display-sm content-base-primary">{card.value}</p>
              <p className="text-body-regular-sm content-action-neutral">{card.subtitle}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
